/**
 * Sentry Configuration for FOH Client
 * 
 * Error monitoring and performance tracking for the React frontend.
 * Currently disabled - will be enabled when implementation is finalized.
 */

import React from 'react';
import * as Sentry from '@sentry/react';
import { ANALYTICS_CONFIG, APP_CONFIG } from './env.js';

/**
 * Initialize Sentry for React error monitoring
 */
export function initSentry() {
    const { sentry } = ANALYTICS_CONFIG;

    // Only initialize if DSN is provided and enabled
    if (!sentry.dsn || !sentry.enabled) {
        console.log('🔇 Sentry monitoring disabled for FOH Client');
        return;
    }

    Sentry.init({
        dsn: sentry.dsn,
        environment: sentry.environment,
        release: `${APP_CONFIG.name}@${APP_CONFIG.version}`,

        // Performance monitoring
        tracesSampleRate: sentry.config.tracesSampleRate,

        // Session replay
        replaysSessionSampleRate: sentry.config.replaysSessionSampleRate,
        replaysOnErrorSampleRate: sentry.config.replaysOnErrorSampleRate,

        integrations: [
            // Browser profiling
            Sentry.browserProfilingIntegration(),

            // Session replay
            Sentry.replayIntegration(),

            // React router integration
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect: React.useEffect,
                useLocation: () => window.location,
                useNavigationType: () => 'navigate'
            }),

            // Browser tracing
            Sentry.browserTracingIntegration(),
        ],

        // Filter out development noise
        beforeSend(event) {
            // Don't send events in development unless explicitly enabled
            if (sentry.environment === 'development' && !window.DEBUG_SENTRY) {
                return null;
            }
            return event;
        },

        // Initial scope
        initialScope: {
            tags: {
                component: 'foh-client',
                service: 'ordering-system'
            }
        }
    });

    console.log('🔍 Sentry monitoring initialized for FOH Client');
}

/**
 * Capture an exception with additional context
 * @param {Error} error - The error to capture
 * @param {Object} context - Additional context
 */
export function captureException(error, context = {}) {
    if (!ANALYTICS_CONFIG.sentry.enabled) {
        console.error('Sentry disabled, logging error:', error);
        return;
    }

    Sentry.withScope((scope) => {
        // Add context
        Object.entries(context).forEach(([key, value]) => {
            scope.setContext(key, value);
        });

        // Add user context if available
        const user = getCurrentUser();
        if (user) {
            scope.setUser({
                id: user.id,
                email: user.email
            });
        }

        // Capture the exception
        Sentry.captureException(error);
    });
}

/**
 * Capture a message with level
 * @param {string} message - The message to capture
 * @param {string} level - The severity level
 * @param {Object} context - Additional context
 */
export function captureMessage(message, level = 'info', context = {}) {
    if (!ANALYTICS_CONFIG.sentry.enabled) {
        console.log(`Sentry disabled, logging message [${level}]:`, message);
        return;
    }

    Sentry.withScope((scope) => {
        // Add context
        Object.entries(context).forEach(([key, value]) => {
            scope.setContext(key, value);
        });

        // Capture the message
        Sentry.captureMessage(message, level);
    });
}

/**
 * Set user context for error tracking
 * @param {Object} user - User information
 */
export function setUser(user) {
    if (!ANALYTICS_CONFIG.sentry.enabled) return;

    Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name
    });
}

/**
 * Add breadcrumb for debugging
 * @param {string} message - Breadcrumb message
 * @param {string} category - Breadcrumb category
 * @param {Object} data - Additional data
 */
export function addBreadcrumb(message, category = 'user', data = {}) {
    if (!ANALYTICS_CONFIG.sentry.enabled) return;

    Sentry.addBreadcrumb({
        message,
        category,
        level: 'info',
        data
    });
}

/**
 * Higher-order component to wrap components with error boundary
 */
export function withSentryErrorBoundary(Component, options = {}) {
    if (!ANALYTICS_CONFIG.sentry.enabled) {
        return Component;
    }

    return Sentry.withErrorBoundary(Component, {
        fallback: ({ error, resetError }) => (
            <div className="error-boundary">
                <h2>Something went wrong</h2>
                <p>{error?.message}</p>
                <button onClick={resetError}>Try again</button>
            </div>
        ),
        beforeCapture: (scope) => {
            scope.setTag('errorBoundary', true);
            scope.setLevel('error');
        },
        ...options
    });
}

// Helper function to get current user (will be implemented with auth)
function getCurrentUser() {
    // TODO: Implement when user authentication is added
    return null;
}
